import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { ALL_RESTAURANTS_ID, useAppUserDefaultStore } from '../../../../stores/appUserDefaultStore';
import { isPracticeActive } from '../../../onboarding/practice/practiceGuards';
import { runDishes, runProgress } from './automationRunUtils';

export default function AutomationRunBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const restaurantId = useAppUserDefaultStore(s => s.restaurant?.id);
  const runs = useCostPriceStore(s => s.automationRuns);
  const dishes = useCostPriceStore(s => s.costPriceList);
  useEffect(() => {
    if (!restaurantId || restaurantId === ALL_RESTAURANTS_ID || isPracticeActive()) return;
    const store = useCostPriceStore.getState();
    if (store.automationRunsRestaurantId !== restaurantId) void store.loadAutomationRuns(restaurantId);
  }, [restaurantId]);
  const scopedRuns = runs.filter(run => run.restaurantId === restaurantId && !run.completedAt);
  if (isPracticeActive()) return null;
  return <>{scopedRuns.map(run => {
    const { done, total } = runProgress(runDishes(run.id, dishes));
    if (!total) return null;
    return <section key={run.id} className="my-6 grid grid-cols-[24px_minmax(0,1fr)] items-start gap-x-3 gap-y-4 rounded-xl border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 p-4 sm:grid-cols-[24px_minmax(0,1fr)_auto] sm:gap-x-4 sm:p-5">
      <AlertCircle className="mt-0.5 h-6 w-6 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <h2 className="font-semibold text-zinc-900 dark:text-zinc-100">{t('costPrice.automationRun.bannerTitle')}{scopedRuns.length > 1 ? ` · ${run.name}` : ''}</h2>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">{t('costPrice.automationRun.bannerProgress', { done, total })}</p>
        <div role="progressbar" aria-label={run.name} aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} className="mt-3 h-1.5 overflow-hidden rounded-full bg-amber-100 dark:bg-amber-900/50">
          <div className="h-full rounded-full bg-amber-500 dark:bg-amber-400" style={{ width: `${done / total * 100}%` }} />
        </div>
      </div>
      <button type="button" onClick={() => navigate(`/costprice/automation-review/${run.id}`)} className="col-start-2 flex min-h-[44px] shrink-0 sm:col-start-3 sm:self-center items-center justify-center gap-2 font-medium text-amber-700 dark:text-amber-300 underline underline-offset-4">
        {t('costPrice.automationRun.continueReview')}<ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>;
  })}</>;
}
